"use client";

import { ExpertOpinionSection } from "@/app/utilities/types";
import BodyContent from "@/component/ui/BodyContent/BodyContent";
import ExpertOpinionTabs from "@/component/ui/ExpertReview/ExpertOpinionTabs";
import Heading from "@/component/ui/Heading";
import Image from "next/image";
import ImageWrapper from "@/component/ui/ImageWrapper";
import React, { useState, useRef } from "react";

interface ExpertOpinionProps {
  title: string;
  subtitle?: string;
  expertOpinionData: ExpertOpinionSection[];
  isMobile?: boolean;
}

const ExpertOpinion: React.FC<ExpertOpinionProps> = ({
  title,
  subtitle,
  expertOpinionData,
  isMobile = false,
}) => {
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const tabsWrapperRef = useRef<HTMLDivElement>(null);
  
  const [activeTab, setActiveTab] = useState<string>(
    expertOpinionData[0]?.id ?? "",
  );

  const tabs = expertOpinionData.map((s) => ({ id: s.id, label: s.label }));
  const activeSectionId = expertOpinionData.some((s) => s.id === activeTab)
    ? activeTab
    : expertOpinionData[0]?.id;

  if (!activeSectionId) return null;

  const handleTabChange = (id: string) => {
    setActiveTab(id);

    if (contentAreaRef.current && tabsWrapperRef.current) {
      const isMobile = window.innerWidth < 768;
      const siteHeaderHeight = isMobile ? 110 : 132;
      
      const actualTabsHeight = tabsWrapperRef.current.offsetHeight;
      const buffer = 16; 

      const totalOffset = siteHeaderHeight + actualTabsHeight + buffer;
      const elementTop = contentAreaRef.current.getBoundingClientRect().top + window.pageYOffset;
      const scrollTarget = elementTop - totalOffset;

      window.scrollTo({
        top: scrollTarget,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="bg-[#F9F9F9] rounded-2xl border border-[#D8DEE8] p-3 md:p-5 pb-4">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <Heading
            tag="h2"
            className="!text-[18px] md:!text-[24px] font-bold text-gray-900"
          >
            {title}
          </Heading>
        </div>
        <Image
          src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}achievement.svg`}
          alt="achievement-icon"
          title="achievement-icon"
          width={32}
          height={32}
        />
      </div>
      <div 
        ref={tabsWrapperRef}
        className="sticky top-[110px] md:top-[132px] z-1 bg-[#F9F9F9] py-2 mb-4 -mx-1 px-1"
      >
        <ExpertOpinionTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      <div className="relative body-content-wrapper" ref={contentAreaRef}>
        {expertOpinionData.map((section) => {
          const isActive = section.id === activeSectionId;

          return (
            <div
              key={section.id}
              id={`expert-opinion-panel-${section.id}`}
              role="tabpanel"
              aria-label={section.label}
              hidden={!isActive}
              className={isActive ? "block" : "hidden"}
            >
              {/* ── Hero image ── */}
              {section.imageUrl && (
                <div className="relative mb-3.5 w-full aspect-video overflow-hidden rounded-xl bg-gray-100">
                  <ImageWrapper
                    src={section.imageUrl}
                    alt={`${title} — ${section.label}`}
                    title={`${title} — ${section.label}`}
                    fill
                    sizes="(max-width: 768px) 100vw, 800px"
                    sourceWidth={isMobile ? 400 : 800}
                    sourceHeight={isMobile ? 225 : 450}
                    className="object-cover"
                  />
                </div>
              )}

              <BodyContent
                fullText={section.content}
                containerClassName=""
                contecttextclass="!text-[14px] !leading-[24px]"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExpertOpinion;